import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { SUCCESS_MESSAGES } from 'request-responses';
import { Lead, LeadState, WaitlistedStages } from 'models/Lead';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import LeadsContext from '../../providers/Leads';
import { getUserFullName } from 'utils';

import './WidthrawLeadModal.styles.scss';

const FIRST_INDEX = 0;

const WITHDRAWN_TO_STAGES: Record<LeadState, WaitlistedStages> = {
  enquire: 'enquireWaitlisted',
  enquireWaitlisted: 'enquireWaitlisted',
  accept: 'acceptWaitlisted',
  acceptWaitlisted: 'acceptWaitlisted',
  confirm: 'confirmWaitlisted',
  confirmWaitlisted: 'confirmWaitlisted',
  offer: 'offerWaitlisted',
  offerWaitlisted: 'offerWaitlisted',
};

type WidthrawLeadModalProps = {
  className?: string;
  lead?: Lead;
};

const WidthrawLeadModal: ForwardRefRenderFunction<
  OverlayRef,
  WidthrawLeadModalProps
> = (props, ref) => {
  const { className, lead } = props;

  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const [isLoading, setIsLoading] = useState(false);

  useImperativeHandle(ref, () => modal, [modal]);

  const { changeState } = useContext(LeadsContext);
  const { popSuccess } = useContext(PopNotificationsContext);
  const { t } = useTranslation();

  const classes = classNames('widthraw-lead-modal', className);
  const { firstName, lastName, email } = lead || {};

  const leadName =
    firstName && lastName ? getUserFullName(firstName, lastName) : email;

  const hanldeWithdraw = useCallback(
    async (values: { revokeDataRoomAccess: boolean }) => {
      const { revokeDataRoomAccess } = values;

      const newLeadState = WITHDRAWN_TO_STAGES[lead?.leadState];

      try {
        setIsLoading(true);
        await changeState(
          lead,
          newLeadState,
          FIRST_INDEX,
          false,
          revokeDataRoomAccess,
        );
        popSuccess(t(SUCCESS_MESSAGES.WITHDRAW_LEAD));
      } catch {
        return;
      } finally {
        setIsLoading(false);
        modal?.close();
      }
    },
    [lead, modal, changeState, popSuccess, t],
  );

  return (
    <Modal ref={modalRef} className={classes}>
      <Form
        initialValues={{ revokeDataRoomAccess: false }}
        onSubmit={hanldeWithdraw}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <p>{t('withdrawInterestLeadTitle', { lead: leadName })}</p>

            <Field
              name="revokeDataRoomAccess"
              type="checkbox"
              label={t('alsoRevokeDataRoomAndFileAccess')}
              component={CheckboxField}
            />

            <div className="actions">
              <Button type="submit" disabled={isLoading}>
                {t('yesWithdrawInterest')}
              </Button>
              <Button
                variant="outline"
                disabled={isLoading}
                onClick={modal?.close}
              >
                {t('cancelWithdrawal')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(WidthrawLeadModal);
